import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Search, ShoppingCart, User, Boxes } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAuthContext } from '@/lib/AuthProvider';
import AuthModalWrapper from './AuthModalWrapper';

const MobileBottomNav = () => {
  const location = useLocation();
  const { getCartCount } = useCart();
  const cartItemCount = getCartCount();
  const isMobile = useIsMobile();
  const { user } = useAuthContext();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authTab, setAuthTab] = useState<"login" | "register">("login");

  const openAuthModal = (tab: "login" | "register") => {
    setAuthTab(tab);
    setAuthModalOpen(true);
  };

  const handleProfileClick = () => {
    if (user) {
      // User is authenticated, navigate to settings
      return '/settings';
    } else {
      // User is not authenticated, show auth modal
      openAuthModal('login');
      return null; // Prevent navigation
    }
  };

  const navItems = [
    {
      label: 'Home',
      icon: Home,
      path: '/',
      isActive: location.pathname === '/'
    },
    {
      label: 'Browse',
      icon: Search,
      path: '/products',
      isActive: location.pathname.startsWith('/products')
    },
    {
      label: 'Combos',
      icon: Boxes,
      path: '/bundles',
      isActive: location.pathname.startsWith('/bundle') || location.pathname === '/bundles'
    },
    {
      label: 'Cart',
      icon: ShoppingCart,
      path: '/cart',
      isActive: location.pathname === '/cart',
      badge: cartItemCount > 0 ? cartItemCount : null
    },
    {
      label: 'Profile',
      icon: User,
      path: user ? '/settings' : null,
      isActive: location.pathname === '/settings',
      onClick: user ? undefined : () => openAuthModal('login')
    }
  ];

  if (!isMobile) {
    return null;
  }

  return (
    <>
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40">
        <div className="flex items-center justify-around py-2">
          {navItems.map((item) => {
            const IconComponent = item.icon;
            
            // For profile item when user is not authenticated, render a button instead of Link
            if (item.label === 'Profile' && !user) {
              return (
                <button
                  key={item.label}
                  onClick={item.onClick}
                  className={`flex flex-col items-center justify-center py-2 px-3 relative transition-colors duration-200 ${
                    item.isActive
                      ? 'text-jamaica-green'
                      : 'text-gray-500 hover:text-jamaica-green'
                  }`}
                >
                  <div className="relative">
                    <IconComponent 
                      className="h-6 w-6" 
                    />
                    {item.badge && (
                      <div className="absolute -top-2 -right-2 bg-jamaica-yellow text-jamaica-black text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold z-[60]">
                        {item.badge > 99 ? '99+' : item.badge}
                      </div>
                    )}
                  </div>
                  <span className={`text-xs mt-1 font-medium ${
                    item.isActive ? 'text-jamaica-green' : 'text-gray-500'
                  }`}>
                    {item.label}
                  </span>
                  {item.isActive && (
                    <div className="absolute -top-0.5 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-jamaica-green rounded-full"></div>
                  )}
                </button>
              );
            }
            
            // For all other items, render as Link
            return (
              <Link
                key={item.path || item.label}
                to={item.path}
                className={`flex flex-col items-center justify-center py-2 px-3 relative transition-colors duration-200 ${
                  item.isActive
                    ? 'text-jamaica-green'
                    : 'text-gray-500 hover:text-jamaica-green'
                }`}
              >
                <div className="relative">
                  <IconComponent 
                    className="h-6 w-6" 
                  />
                  {item.badge && (
                    <div className="absolute -top-2 -right-2 bg-jamaica-yellow text-jamaica-black text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold z-[60]">
                      {item.badge > 99 ? '99+' : item.badge}
                    </div>
                  )}
                </div>
                <span className={`text-xs mt-1 font-medium ${
                  item.isActive ? 'text-jamaica-green' : 'text-gray-500'
                }`}>
                  {item.label}
                </span>
                {item.isActive && (
                  <div className="absolute -top-0.5 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-jamaica-green rounded-full"></div>
                )}
              </Link>
            );
          })}
        </div>
      </nav>
      
      {/* Auth Modal */}
      <AuthModalWrapper
        isOpen={authModalOpen}
        onOpenChange={setAuthModalOpen}
        defaultTab={authTab}
      />
    </>
  );
};

export default MobileBottomNav;
